import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api/blog'
  : 'https://bears.playlistpartner.com/api/blog';

interface PipelineStatus {
  id: string;
  status: 'processing' | 'completed' | 'failed';
  currentStep: string;
  headline?: string;
  publishDate?: string;
  isDraft?: boolean;
  error?: string;
  failedStep?: string;
}

export function BlogSubmitPage() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('blog-password') || '');
  const [isUnlocked, setIsUnlocked] = useState(() => !!sessionStorage.getItem('blog-password'));
  const [url, setUrl] = useState('');
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUnlock = () => {
    if (password.trim()) {
      sessionStorage.setItem('blog-password', password.trim());
      setIsUnlocked(true);
    }
  };

  const handleSubmit = async () => {
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-blog-password': password,
        },
        body: JSON.stringify({ googleDocUrl: url.trim() }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401) {
          sessionStorage.removeItem('blog-password');
          setIsUnlocked(false);
          setSubmitError('Invalid password. Please try again.');
        } else {
          setSubmitError(data.error || 'Submission failed');
        }
        return;
      }

      setPipelineId(data.pipelineId);
      setPipelineStatus({ id: data.pipelineId, status: 'processing', currentStep: 'Initializing' });
    } catch (err) {
      setSubmitError('Network error. Is the API server running?');
    } finally {
      setIsSubmitting(false);
    }
  };

  const pollStatus = useCallback(async () => {
    if (!pipelineId) return;

    try {
      const res = await fetch(`${API_BASE}/status/${pipelineId}`, {
        headers: { 'x-blog-password': password },
      });
      if (res.ok) {
        const data: PipelineStatus = await res.json();
        setPipelineStatus(data);
      }
    } catch {
      // Ignore polling errors
    }
  }, [pipelineId, password]);

  useEffect(() => {
    if (!pipelineId || pipelineStatus?.status !== 'processing') return;

    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [pipelineId, pipelineStatus?.status, pollStatus]);

  // Password gate
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-sm w-full">
          <h1 className="text-xl font-bold mb-4 text-center">Bypass Submission</h1>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleUnlock()}
            placeholder="Enter password"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            autoFocus
          />
          <button
            onClick={handleUnlock}
            className="w-full mt-4 bg-green-500 text-white py-3 rounded-lg font-medium hover:bg-green-600 transition-colors"
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-2">Bypass Submission</h1>
        <p className="text-gray-600 mb-8">Paste a Google Docs Link</p>

        {/* Submission form */}
        {!pipelineId && (
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Doc URL
            </label>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !isSubmitting && url.trim() && handleSubmit()}
              placeholder="https://docs.google.com/document/d/..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              autoFocus
            />
            {submitError && (
              <p className="mt-2 text-red-600 text-sm">{submitError}</p>
            )}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !url.trim()}
              className="w-full mt-4 bg-green-500 text-white py-3 rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Process Newsletter'}
            </button>
          </div>
        )}

        {/* Pipeline status */}
        {pipelineStatus && (
          <div className="bg-white p-6 rounded-xl shadow-lg">
            {pipelineStatus.status === 'processing' && (
              <div className="text-center">
                <div className="inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-lg font-medium">{pipelineStatus.currentStep}</p>
                <p className="text-gray-500 text-sm mt-1">This usually takes 15-30 seconds</p>
              </div>
            )}

            {pipelineStatus.status === 'completed' && (
              <div className="text-center">
                <div className="text-green-500 text-4xl mb-3">&#10003;</div>
                <p className="text-lg font-bold">{pipelineStatus.headline}</p>
                <p className="text-gray-600 mt-2">
                  {pipelineStatus.isDraft
                    ? 'Created as draft in Contentful'
                    : `Scheduled for ${new Date(pipelineStatus.publishDate!).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
                  }
                </p>
                <button
                  onClick={() => { setPipelineId(null); setPipelineStatus(null); setUrl(''); }}
                  className="mt-6 bg-green-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-600 transition-colors"
                >
                  Submit Another
                </button>
              </div>
            )}

            {pipelineStatus.status === 'failed' && (
              <div className="text-center">
                <div className="text-red-500 text-4xl mb-3">&#10007;</div>
                <p className="text-lg font-bold text-red-600">Pipeline Failed</p>
                <p className="text-gray-600 mt-1">Step: {pipelineStatus.failedStep}</p>
                <p className="text-red-500 text-sm mt-2">{pipelineStatus.error}</p>
                <button
                  onClick={() => { setPipelineId(null); setPipelineStatus(null); }}
                  className="mt-6 bg-green-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-600 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
